// src/pages/user-role-administration/components/UserDirectory.jsx
import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserDirectory = ({ 
  users, 
  selectedUsers, 
  selectedUser, 
  onUserSelect, 
  onBulkSelect, 
  searchTerm, 
  onSearchChange, 
  filterOptions, 
  onFilterChange 
}) => {
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [isUserDetailModalOpen, setIsUserDetailModalOpen] = useState(false);

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users?.filter(user => {
      const matchesSearch = !searchTerm || 
        user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.currentRole?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      const matchesDepartment = !filterOptions?.department || user?.department === filterOptions?.department;
      const matchesRole = !filterOptions?.role || user?.currentRole === filterOptions?.role;
      const matchesStatus = !filterOptions?.status || user?.status === filterOptions?.status;

      return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
    });

    // Sort users
    filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];

      if (sortBy === 'lastLogin' && aValue !== 'Never' && bValue !== 'Never') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [users, searchTerm, filterOptions, sortBy, sortOrder]);

  const departments = [...new Set(users?.map(user => user?.department))];
  const roles = [...new Set(users?.map(user => user?.currentRole))];
  const statuses = ['active', 'inactive', 'pending', 'suspended'];

  const handleSelectAll = (checked) => {
    if (checked) {
      onBulkSelect(filteredAndSortedUsers?.map(user => user?.id));
    } else {
      onBulkSelect([]);
    }
  };

  const handleUserCheck = (userId, checked) => {
    if (checked) {
      onBulkSelect([...selectedUsers, userId]);
    } else {
      onBulkSelect(selectedUsers?.filter(id => id !== userId));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success-dark bg-success-dark bg-opacity-20';
      case 'inactive': return 'text-text-dark-muted bg-dark-elevated';
      case 'pending': return 'text-warning-dark bg-warning-dark bg-opacity-20';
      case 'suspended': return 'text-error-dark bg-error-dark bg-opacity-20';
      default: return 'text-text-dark-secondary bg-dark-secondary';
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const UserDetailModal = ({ user, isOpen, onClose }) => {
    if (!isOpen || !user) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-modal p-4">
        <div className="neumorphic-card w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <img
                  src={user?.avatar}
                  alt={`${user?.name} profile`}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <h2 className="text-xl font-semibold text-text-dark-primary">{user?.name}</h2>
                  <p className="text-text-dark-secondary">{user?.currentRole}</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user?.status)}`}>
                    {user?.status?.charAt(0)?.toUpperCase() + user?.status?.slice(1)}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-text-dark-secondary hover:text-text-dark-primary hover:bg-dark-elevated hover:bg-opacity-30 transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* User Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-text-dark-primary border-b border-dark-border pb-2">
                  Contact Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-text-dark-secondary">Email</label>
                    <p className="text-text-dark-primary">{user?.email}</p>
                  </div>
                  <div>
                    <label className="text-sm text-text-dark-secondary">Phone</label>
                    <p className="text-text-dark-primary">{user?.phoneNumber || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-text-dark-secondary">Location</label>
                    <p className="text-text-dark-primary">{user?.location || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Work Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-text-dark-primary border-b border-dark-border pb-2">
                  Work Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-text-dark-secondary">Employee ID</label>
                    <p className="text-text-dark-primary">{user?.employeeId}</p>
                  </div>
                  <div>
                    <label className="text-sm text-text-dark-secondary">Department</label>
                    <p className="text-text-dark-primary">{user?.department}</p>
                  </div>
                  <div>
                    <label className="text-sm text-text-dark-secondary">Team</label>
                    <p className="text-text-dark-primary">{user?.team || 'Not assigned'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-text-dark-secondary">Manager</label>
                    <p className="text-text-dark-primary">{user?.manager || 'Not assigned'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-text-dark-secondary">Hire Date</label>
                    <p className="text-text-dark-primary">{new Date(user?.hireDate)?.toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Security Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-text-dark-primary border-b border-dark-border pb-2">
                  Security & Access
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-text-dark-secondary">Access Level</label>
                    <p className="text-text-dark-primary">{user?.accessLevel}</p>
                  </div>
                  <div>
                    <label className="text-sm text-text-dark-secondary">Last Login</label>
                    <p className="text-text-dark-primary">
                      {user?.lastLogin === 'Never' ? 'Never' : new Date(user?.lastLogin)?.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-text-dark-secondary">Password Expiry</label>
                    <p className="text-text-dark-primary">{new Date(user?.passwordExpiry)?.toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm text-text-dark-secondary">MFA Enabled</label>
                    <p className={`text-sm font-medium ${user?.mfaEnabled ? 'text-success-dark' : 'text-error-dark'}`}>
                      {user?.mfaEnabled ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-text-dark-secondary">Account Locked</label>
                    <p className={`text-sm font-medium ${user?.accountLocked ? 'text-error-dark' : 'text-success-dark'}`}>
                      {user?.accountLocked ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Permissions */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-text-dark-primary border-b border-dark-border pb-2">
                  Permissions
                </h3>
                <div className="space-y-2">
                  {user?.permissions?.map(permission => (
                    <span 
                      key={permission}
                      className="inline-block px-3 py-1 bg-neon-green bg-opacity-20 text-neon-green rounded-full text-xs font-medium mr-2 mb-2"
                    >
                      {permission?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="mt-6 space-y-4">
              <h3 className="text-lg font-medium text-text-dark-primary border-b border-dark-border pb-2">
                Recent Activity
              </h3>
              <div className="space-y-3">
                {user?.recentActivity?.slice(0, 5)?.map((activity, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-dark-secondary rounded-lg">
                    <div>
                      <p className="text-text-dark-primary text-sm">{activity?.action}</p>
                      <p className="text-text-dark-secondary text-xs">
                        {new Date(activity?.timestamp)?.toLocaleString()}
                      </p>
                    </div>
                    <Icon name="Activity" size={16} className="text-text-dark-muted" />
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-dark-border">
              <button className="btn-secondary-dark inline-flex items-center space-x-2">
                <Icon name="Edit" size={16} />
                <span>Edit User</span>
              </button>
              <button className="btn-secondary-dark inline-flex items-center space-x-2">
                <Icon name="Key" size={16} />
                <span>Reset Password</span>
              </button>
              <button 
                className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  user?.status === 'active' ?'bg-error-dark hover:bg-error-dark hover:bg-opacity-80 text-white' :'bg-success-dark hover:bg-success-dark hover:bg-opacity-80 text-white'
                }`}
              >
                <Icon name={user?.status === 'active' ? 'UserX' : 'UserCheck'} size={16} />
                <span>{user?.status === 'active' ? 'Deactivate' : 'Activate'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header & Controls */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <Input
              placeholder="Search users by name, email, department, or role..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e?.target?.value)}
              icon="Search"
            />
          </div>

          {/* View Toggle */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'grid' ?'bg-neon-green text-white' :'text-text-dark-secondary hover:text-text-dark-primary hover:bg-dark-elevated hover:bg-opacity-30'
              }`}
            >
              <Icon name="Grid3X3" size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'list' ?'bg-neon-green text-white' :'text-text-dark-secondary hover:text-text-dark-primary hover:bg-dark-elevated hover:bg-opacity-30'
              }`}
            >
              <Icon name="List" size={16} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <Select
            placeholder="All Departments"
            value={filterOptions?.department}
            onChange={(value) => onFilterChange({...filterOptions, department: value})}
            options={departments?.map(dept => ({ value: dept, label: dept }))}
          />
          <Select
            placeholder="All Roles"
            value={filterOptions?.role}
            onChange={(value) => onFilterChange({...filterOptions, role: value})}
            options={roles?.map(role => ({ value: role, label: role }))}
          />
          <Select
            placeholder="All Statuses"
            value={filterOptions?.status}
            onChange={(value) => onFilterChange({...filterOptions, status: value})}
            options={statuses?.map(status => ({ value: status, label: status?.charAt(0)?.toUpperCase() + status?.slice(1) }))}
          />
          <button
            onClick={() => onFilterChange({ department: '', role: '', status: '', lastLogin: '' })}
            className="btn-secondary-dark inline-flex items-center justify-center space-x-2"
          >
            <Icon name="X" size={16} />
            <span>Clear Filters</span>
          </button>
        </div>

        {/* Bulk Selection */}
        {filteredAndSortedUsers?.length > 0 && (
          <div className="flex items-center justify-between mt-4 p-4 bg-dark-secondary rounded-lg">
            <div className="flex items-center space-x-4">
              <Checkbox
                checked={selectedUsers?.length === filteredAndSortedUsers?.length}
                onChange={handleSelectAll}
                indeterminate={selectedUsers?.length > 0 && selectedUsers?.length < filteredAndSortedUsers?.length}
              />
              <span className="text-text-dark-primary">
                {selectedUsers?.length > 0 
                  ? `${selectedUsers?.length} of ${filteredAndSortedUsers?.length} users selected`
                  : `Select all ${filteredAndSortedUsers?.length} users`
                }
              </span>
            </div>
            
            {/* Sort Controls */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-text-dark-secondary">Sort by:</span>
              <Select
                value={sortBy}
                onChange={setSortBy}
                options={[
                  { value: 'name', label: 'Name' },
                  { value: 'department', label: 'Department' },
                  { value: 'currentRole', label: 'Role' },
                  { value: 'status', label: 'Status' },
                  { value: 'lastLogin', label: 'Last Login' }
                ]}
              />
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="p-2 rounded-lg text-text-dark-secondary hover:text-text-dark-primary hover:bg-dark-elevated hover:bg-opacity-30 transition-colors"
              >
                <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Users Display */}
      {filteredAndSortedUsers?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="mx-auto text-text-dark-muted mb-4" />
          <h3 className="text-lg font-medium text-text-dark-primary mb-2">No users found</h3>
          <p className="text-text-dark-secondary">
            {searchTerm || Object.values(filterOptions)?.some(v => v) 
              ? 'Try adjusting your search or filters' :'No users have been added yet'
            }
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAndSortedUsers?.map(user => (
            <div key={user?.id} className="neumorphic-card p-6 hover:bg-dark-elevated hover:bg-opacity-30 transition-colors">
              {/* Selection Checkbox */}
              <div className="flex items-start justify-between mb-4">
                <Checkbox
                  checked={selectedUsers?.includes(user?.id)}
                  onChange={(checked) => handleUserCheck(user?.id, checked)}
                />
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user?.status)}`}>
                  {user?.status?.charAt(0)?.toUpperCase() + user?.status?.slice(1)}
                </span>
              </div>

              {/* User Info */}
              <div className="text-center mb-4">
                <img
                  src={user?.avatar}
                  alt={`${user?.name} profile`}
                  className="w-16 h-16 rounded-full mx-auto mb-3 object-cover"
                />
                <h3 className="text-lg font-semibold text-text-dark-primary mb-1">{user?.name}</h3>
                <p className="text-text-dark-secondary text-sm mb-1">{user?.currentRole}</p>
                <p className="text-text-dark-muted text-xs">{user?.department}</p>
              </div>

              {/* Quick Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-dark-secondary">Last Login:</span>
                  <span className="text-text-dark-primary">
                    {user?.lastLogin === 'Never' ? 'Never' : new Date(user?.lastLogin)?.toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-dark-secondary">MFA:</span>
                  <span className={`font-medium ${user?.mfaEnabled ? 'text-success-dark' : 'text-error-dark'}`}>
                    {user?.mfaEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    onUserSelect(user);
                    setIsUserDetailModalOpen(true);
                  }}
                  className="btn-secondary-dark text-sm inline-flex items-center space-x-1"
                >
                  <Icon name="Eye" size={14} />
                  <span>View</span>
                </button>
                <button className="btn-secondary-dark text-sm inline-flex items-center space-x-1">
                  <Icon name="Edit" size={14} />
                  <span>Edit</span>
                </button>
                <button className="btn-secondary-dark text-sm inline-flex items-center space-x-1">
                  <Icon name="MoreHorizontal" size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // List View
        (<div className="space-y-2">
          {filteredAndSortedUsers?.map(user => (
            <div key={user?.id} className="neumorphic-card p-4 hover:bg-dark-elevated hover:bg-opacity-30 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Checkbox
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={(checked) => handleUserCheck(user?.id, checked)}
                  />
                  <img
                    src={user?.avatar}
                    alt={`${user?.name} profile`}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-semibold text-text-dark-primary">{user?.name}</h3>
                    <p className="text-text-dark-secondary text-sm">{user?.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-6">
                  <div className="text-center">
                    <p className="text-text-dark-primary text-sm font-medium">{user?.department}</p>
                    <p className="text-text-dark-secondary text-xs">Department</p>
                  </div>
                  <div className="text-center">
                    <p className="text-text-dark-primary text-sm font-medium">{user?.currentRole}</p>
                    <p className="text-text-dark-secondary text-xs">Role</p>
                  </div>
                  <div className="text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user?.status)}`}>
                      {user?.status?.charAt(0)?.toUpperCase() + user?.status?.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        onUserSelect(user);
                        setIsUserDetailModalOpen(true);
                      }}
                      className="p-2 rounded-lg text-text-dark-secondary hover:text-text-dark-primary hover:bg-dark-elevated hover:bg-opacity-30 transition-colors"
                    >
                      <Icon name="Eye" size={16} />
                    </button>
                    <button className="p-2 rounded-lg text-text-dark-secondary hover:text-text-dark-primary hover:bg-dark-elevated hover:bg-opacity-30 transition-colors">
                      <Icon name="Edit" size={16} />
                    </button>
                    <button className="p-2 rounded-lg text-text-dark-secondary hover:text-text-dark-primary hover:bg-dark-elevated hover:bg-opacity-30 transition-colors">
                      <Icon name="MoreHorizontal" size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>)
      )}
      {/* User Detail Modal */}
      <UserDetailModal
        user={selectedUser}
        isOpen={isUserDetailModalOpen}
        onClose={() => setIsUserDetailModalOpen(false)}
      />
    </div>
  );
};

export default UserDirectory;